import { useQuery } from '@tanstack/react-query';
import { Navigate, useNavigate } from 'react-router-dom';
import { GraduationCap, LogOut, School, Users } from 'lucide-react';
import {
  getGuardianToken,
  GuardianApi,
  GuardianChild,
  setGuardianToken,
} from '@/lib/guardian-api';

export function GuardianChildrenPage() {
  const navigate = useNavigate();
  const token = getGuardianToken();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['guardian-children'],
    queryFn: GuardianApi.children,
    enabled: !!token,
    retry: false,
  });

  if (!token) return <Navigate to="/guardian/login" replace />;

  const logout = () => {
    setGuardianToken(null);
    navigate('/guardian/login', { replace: true });
  };

  if (isError) {
    setGuardianToken(null);
    return <Navigate to="/guardian/login" replace />;
  }

  const children = data ?? [];
  const bySchool = new Map<string, { schoolName: string; children: GuardianChild[] }>();
  for (const c of children) {
    const group = bySchool.get(c.schoolId) ?? { schoolName: c.schoolName, children: [] };
    group.children.push(c);
    bySchool.set(c.schoolId, group);
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="flex items-center justify-between border-b border-slate-200 bg-white px-5 py-3">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
            <Users className="h-4 w-4" />
          </div>
          <div className="text-sm font-semibold text-slate-900">Guardian Portal</div>
        </div>
        <button
          onClick={logout}
          className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-100"
        >
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </header>

      <main className="mx-auto max-w-lg space-y-4 px-4 py-6">
        <div className="text-xs font-medium uppercase tracking-wide text-slate-400">
          My children
        </div>

        {isLoading && (
          <div className="card p-6 text-center text-slate-400">Loading…</div>
        )}

        {!isLoading && children.length === 0 && (
          <div className="card p-6 text-center text-sm text-slate-500">
            No children linked to this account yet.
          </div>
        )}

        {Array.from(bySchool.values()).map((group) => (
          <div key={group.schoolName} className="card overflow-hidden">
            <div className="flex items-center gap-2 border-b border-slate-100 bg-brand-50/40 px-5 py-3">
              <School className="h-4 w-4 text-brand-600" />
              <div className="font-semibold text-slate-900">{group.schoolName}</div>
            </div>
            <ul className="divide-y divide-slate-50">
              {group.children.map((c) => (
                <li key={c.studentLegacyId} className="flex items-center gap-3 px-5 py-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-100 text-sm font-bold text-brand-700">
                    <GraduationCap className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="font-medium text-slate-900">{c.studentName}</div>
                    <div className="text-xs text-slate-500">
                      {c.admissionNo}
                      {c.className
                        ? ` · ${[c.className, c.sectionName].filter(Boolean).join(' ')}`
                        : ' · Not enrolled in a class yet'}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </main>
    </div>
  );
}
